import { useCallback, useState } from 'react';

import type { ErrorLike } from '@apollo/client';
import { useMutation } from '@apollo/client/react';

import { type CreateBuyerLeadMutationVariables } from '@graphql/__generated__/graphql';
import { CREATE_BUYER_LEAD } from '@modules/_shared/components/buyer-lead/buyerLeadQueries';
import {
  type BuyerLeadStage,
  buildOriginationActivity,
  getBuyerLeadErrorMessage,
  getBuyerLeadStageOptions,
} from '@modules/_shared/components/buyer-lead/buyerLeadUtils';
import { Drawer } from '@modules/_shared/components/drawer/Drawer';
import {
  type FormDefinitionType,
  RaForm,
} from '@modules/_shared/components/form/RaForm';
import { MutationErrorSnackbar } from '@modules/_shared/components/MutationErrorModal';
import RASnackbar from '@modules/_shared/components/RASnackbar';
import { useAppData } from '@modules/_shared/providers/AppDataProvider';
import { emitFeedRefresh } from '@modules/_shared/utils/feed-refresh';
import { GET_USER_BUYERS_LEAD } from '@modules/users/components/UserBuyerLeadsKanban';
import { useLocale } from '@shared/hooks/locale';

type BuyerLeadCreateDrawerProps = {
  userId: string;
  stages: BuyerLeadStage[];
  onClose: () => void;
  onCreate: (buyerLeadId: string) => void;
};

export const BuyerLeadCreateDrawer = ({
  userId,
  stages,
  onClose,
  onCreate,
}: BuyerLeadCreateDrawerProps) => {
  const { t } = useLocale();
  const { me } = useAppData();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [apolloError, setErrorLike] = useState<ErrorLike | null>(null);

  const defaultStageId = stages[0]?.id ?? null;

  const [insertBuyerLead] = useMutation(CREATE_BUYER_LEAD, {
    refetchQueries: [GET_USER_BUYERS_LEAD],
    onCompleted: data => {
      if (data.insert_buyer_leads_one != null) {
        onClose();
        onCreate(data.insert_buyer_leads_one.id);
        emitFeedRefresh();
      }
    },
    onError: error => setErrorMessage(getBuyerLeadErrorMessage(error, t)),
  });

  const formDefinition = useCallback<
    FormDefinitionType<{
      lotId: string;
      stageId: string;
    }>
  >(
    ({ t }) => [
      {
        type: 'lot',
        name: 'lotId',
        label: t('Listing'),
        required: true,
      },
      {
        type: 'select',
        name: 'stageId',
        label: t('Pipeline step'),
        required: true,
        options: () => getBuyerLeadStageOptions(stages),
      },
    ],
    [stages],
  );

  const handleSubmit = async (data: { lotId: string; stageId: string }) => {
    if (!data.stageId) {
      return;
    }

    setErrorMessage(null);

    const variables: CreateBuyerLeadMutationVariables = {
      buyerLead: {
        lot_id: data.lotId,
        stage_id: data.stageId,
        source: 'manual',
        user_id: userId,
        activities: buildOriginationActivity(
          userId,
          me?.is_admin ? me?.id : undefined,
        ),
      },
    };
    return insertBuyerLead({ variables });
  };

  return (
    <Drawer onClose={onClose} title={t('New buyer lead')} maxWidth={600}>
      <RaForm
        formDefinition={formDefinition}
        defaultValues={{ stageId: defaultStageId }}
        allowSubmitNotDirty
        onCancel={onClose}
        onSubmit={handleSubmit}
      />
      <MutationErrorSnackbar
        error={apolloError}
        onClose={() => setErrorLike(null)}
      />

      {errorMessage != null && (
        <RASnackbar
          message={errorMessage}
          onClose={() => setErrorMessage(null)}
        />
      )}
    </Drawer>
  );
};
